/**
 * TitleRenderer Component
 *
 * Renders title overlays on top of storyboard images.
 * Uses normalized 0-1 coordinates that are converted to absolute
 * pixel positions based on the container size.
 *
 * Similar to TextOverlayRenderer but for independent title layer.
 */

import React, { CSSProperties } from 'react';
import type { TitleNode } from '@/types';
import { presetPositionToCoordinates } from '@/utils/overlayPositions';

export interface TitleRendererProps {
  /** Title node to render */
  titleNode: TitleNode;
  /** Container width in pixels for coordinate conversion */
  containerWidth: number;
  /** Container height in pixels for coordinate conversion */
  containerHeight: number;
}

/**
 * TitleRenderer Component
 *
 * Renders a single title overlay
 */
export function TitleRenderer({
  titleNode,
  containerWidth,
  containerHeight,
}: TitleRendererProps) {
  const { data } = titleNode;
  const opacity = data.opacity !== undefined ? data.opacity : 1;
  const color = data.color || '#FFFFFF';
  const fontFamily = data.fontFamily || 'Arial';
  const alignment = data.alignment || 'center';
  const fontWeight = data.fontWeight || 'normal';
  const fontStyle = data.fontStyle || 'normal';
  
  const baseFontSize = data.fontSize || 48;
  const containerScaleFactor = containerWidth / 800;
  const scaledFontSize = baseFontSize * containerScaleFactor;

  let posX = data.position.x;
  let posY = data.position.y;

  if (data.presetPosition) {
    const presetCoords = presetPositionToCoordinates(data.presetPosition);
    posX = presetCoords.x;
    posY = presetCoords.y;
  }

  const absoluteX = posX * containerWidth;
  const absoluteY = posY * containerHeight;

  let textAlign: CSSProperties['textAlign'] = 'center';
  let transform = 'translate(-50%, -50%)';
  
  if (alignment === 'left') {
    textAlign = 'left';
    transform = 'translate(0, -50%)';
  } else if (alignment === 'right') {
    textAlign = 'right';
    transform = 'translate(-100%, -50%)';
  }

  const containerStyles: CSSProperties = {
    position: 'absolute',
    left: absoluteX,
    top: absoluteY,
    transform,
    transformOrigin: alignment === 'left' ? 'top left' : alignment === 'right' ? 'top right' : 'top center',
    transition: 'none',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    maxWidth: '80%',
    zIndex: 10,
  };

  const textStyles: CSSProperties = {
    fontSize: `${scaledFontSize}px`,
    fontFamily,
    color,
    textAlign,
    fontWeight,
    fontStyle,
    opacity,
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
    lineHeight: 1.2,
    display: 'inline-block',
    width: '100%',
  };

  return (
    <div style={containerStyles} title="Title overlay">
      <span style={textStyles}>{data.text}</span>
    </div>
  );
}

export default TitleRenderer;
